/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Zustand store for batch upload
 */

import _ from 'lodash';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { getValueFromStorage, setValueToStorage } from '@veritable/common/utils';
import { DEFAULT_SLIDE_IN_ANIMATION, DEFAULT_SLIDE_OUT_ANIMATION, DEFAULT_PAGINATION } from '@veritable/common/constants';

const persistedMappingsKey = 'batchMappings';

const defaultValues = {
	currentStep: 0,
	currentDataMappingStep: 0,
	hideLocationMapping: false,
};

const getAnimation = (previous: number, current: number): string => {
	if (previous === current) {
		return '';
	}
	return `${previous > current ? DEFAULT_SLIDE_OUT_ANIMATION : DEFAULT_SLIDE_IN_ANIMATION}`;
};

const initialState: EligibilityBatchUpload.IValues = {
	pageSize: DEFAULT_PAGINATION.pageSize,
	pageNumber: DEFAULT_PAGINATION.pageNumber,
	currentStep: defaultValues.currentStep,
	validRequestsAfterValidation: 0,
	skippedBlobId: undefined,
	stepMeta: {} as EligibilityBatchUpload.IStepMeta,
	mapping: {} as EligibilityBatchUpload.IBatchEntityMappingModel,
	persistedMappings: getValueFromStorage(persistedMappingsKey, {}),
	autoFilledMeta: {},
	animation: '',
	searchParams: {},
	apiParams: {},
	file: undefined,
	validationMeta: {},
	hideLocationMapping: defaultValues.hideLocationMapping,
	currentDataMappingStep: defaultValues.currentDataMappingStep,
	dataMappingStepAnimation: '',
};

const useBatchUploadStore = create<EligibilityBatchUpload.IStore>()(
	persist(
		devtools(
			(set, get) => ({
				...initialState,
				actions: {
					setFile: (file) => set(() => ({ file })),
					setValidationMeta: (validationMeta) => set(() => ({ validationMeta })),
					setPageNumber: (pageNumber) => set(() => ({ pageNumber })),
					setPageSize: (pageSize) => set(() => ({ pageSize })),
					resetStates: () =>
						set(() => ({
							file: undefined,
							validationMeta: {},
							currentStep: defaultValues.currentStep,
							currentDataMappingStep: defaultValues.currentDataMappingStep,
							validRequestsAfterValidation: 0,
							mapping: {} as EligibilityBatchUpload.IBatchEntityMappingModel,
							autoFilledMeta: {},
							hideLocationMapping: defaultValues.hideLocationMapping,
						})),
					setAnimation: (animation) => set(() => ({ animation })),
					setDataMappingStepAnimation: (dataMappingStepAnimation) => set(() => ({ dataMappingStepAnimation })),
					setCurrentDataMappingStep: (currentDataMappingStep) =>
						set((state) => ({
							currentDataMappingStep,
							dataMappingStepAnimation: getAnimation(state.currentDataMappingStep, currentDataMappingStep),
						})),
					setInitialMetaData: (metaData) =>
						set(() => ({
							mapping: metaData.initialMapping,
							autoFilledMeta: metaData.autoFilledDetails,
							hideLocationMapping: metaData.hideLocationMapping,
						})),
					setCurrentStep: (currentStep) =>
						set((state) => ({
							currentStep,
							animation: getAnimation(state.currentStep, currentStep),
						})),
					setValidRequestsAfterValidation: (validRequestsAfterValidation) => set(() => ({ validRequestsAfterValidation })),
					setSkippedBlobId: (skippedBlobId) => set(() => ({ skippedBlobId })),
					setMapping: (mapping) => {
						set(() => ({ mapping }));
						// Automatically update local storage whenever mapping is set
						const state = get();
						if (!_.isEmpty(mapping) && mapping?.batchId) {
							const updatedMappings = {
								...state.persistedMappings,
								[mapping.batchId]: {
									entityMap: mapping.entityMap,
									created: mapping.created,
								} as EligibilityBatchUpload.IBatchEntityMappingModel,
							};

							setValueToStorage(persistedMappingsKey, updatedMappings);
							set(() => ({ persistedMappings: updatedMappings }));
						}
					},
					setPersistedMappings: (persistedMappings) => {
						setValueToStorage(persistedMappingsKey, persistedMappings);
						set(() => ({ persistedMappings }));
					},
					setAutoFilledMeta: (autoFilledMeta) => set(() => ({ autoFilledMeta })),
					setSearchParams: (searchParams) =>
						set(() => ({ searchParams: _.isEmpty(searchParams.filterParams) ? {} : searchParams })),
					setApiParams: (apiParams) =>
						set(() => ({
							apiParams: _.isEmpty(apiParams?.criteria) ? {} : apiParams,
							pageNumber: DEFAULT_PAGINATION.pageNumber,
						})),
				},
			}),
			{ name: 'Batch Upload' },
		),
		{
			name: 'batch-upload',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				pageSize: state.pageSize,
				searchParams: state.searchParams,
				apiParams: state.apiParams,
			}),
		},
	),
);

// Store to return actions and values
const useBatchUploadStoreValues = (): EligibilityBatchUpload.IValues & { stepMeta: EligibilityBatchUpload.IStepMeta } =>
	useBatchUploadStore(useShallow((state) => _.omit(state, ['actions'])));

const useBatchUploadStoreActions = (): EligibilityBatchUpload.IActions => useBatchUploadStore(useShallow((state) => state.actions));

export { DEFAULT_PAGINATION as defaultPagination, useBatchUploadStoreValues, useBatchUploadStoreActions, getAnimation };
export default useBatchUploadStore;
